import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../api";
import "./Assets.css";

// The customer assets page reads the vendor-discovered source of truth (/discovered-assets) —
// the vendor connections feed. Connecting a vendor (AWS, Azure/M365, GitHub, Okta...) downloads its
// assets and they appear here — filterable by vendor and layer.
const LAYERS = ["ALL", "COMPUTE", "IDENTITY", "NETWORK", "DATABASE", "CODE"];

function riskClass(score) {
  // Reuse existing badge styles: amber/red for elevated risk, green for low.
  return score >= 40 ? "as-review" : "as-active";
}

// Risk/blast-radius are only computed for the iASM-modeled subset (id "iasm:...") — the vendor
// discovery source-of-truth ("sot:...") has no risk model yet (DiscoveredAssetController always
// returns 0 for it), so showing "0" there reads as a false "no risk" signal. Distinguish by id prefix.
function hasRiskScore(asset) {
  return typeof asset.id === "string" && asset.id.startsWith("iasm:");
}

// Highest risk first; assets with no risk model yet sort to the bottom (current page only — the
// API paginates server-side, so this can't guarantee a true cross-page ordering).
function byRiskDesc(a, b) {
  const aScored = hasRiskScore(a), bScored = hasRiskScore(b);
  if (aScored !== bScored) return aScored ? -1 : 1;
  return (b.riskScore || 0) - (a.riskScore || 0);
}

function Assets() {
  const { t } = useTranslation();
  const [assets, setAssets]   = useState([]);
  const [counts, setCounts]   = useState({ totalAssets: 0, byCloudProvider: {}, byLayer: {} });
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(0);
  const [size, setSize]       = useState(10);
  const [provider, setProvider] = useState("ALL");
  const [layer, setLayer]     = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [open, setOpen]       = useState(null);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, size };
      if (provider !== "ALL") params.provider = provider;
      if (layer !== "ALL") params.layer = layer;
      const r = await axiosInstance.get("/discovered-assets", { params });
      setAssets((r.data.content || []).slice().sort(byRiskDesc));
      setTotal(r.data.totalElements || 0);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, size, provider, layer]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  // Vendors + layer counts drive the dropdown options and the stat bar (only what actually exists).
  useEffect(() => {
    axiosInstance.get("/discovered-assets/counts")
      .then(r => setCounts(r.data || {}))
      .catch(() => {});
  }, []);

  const providers  = Object.entries(counts.byCloudProvider || {}).map(([name, count]) => ({ name, count }));
  const identities = (counts.byLayer || {}).IDENTITY || 0;

  if (loading && assets.length === 0) return <div className="as-loading"><div className="as-spinner" /></div>;
  if (error) return <div className="as-error">{t("Assets.errorLoadingAssets", { error })}</div>;

  return (
    <div className="as-page">
      {/* Stat bar */}
      <div className="as-stat-bar">
        <div className="as-stat">
          <span className="as-stat-val">{counts.totalAssets || total}</span>
          <span className="as-stat-label">{t("Assets.totalAssets")}</span>
        </div>
        <div className="as-stat">
          <span className="as-stat-val as-val-ok">{providers.length}</span>
          <span className="as-stat-label">{t("Assets.vendors")}</span>
        </div>
        <div className="as-stat">
          <span className="as-stat-val as-val-warn">{identities}</span>
          <span className="as-stat-label">{t("Assets.identities")}</span>
        </div>
      </div>

      {/* Table card */}
      <div className="as-card">
        {/* Toolbar: vendor + layer filters */}
        <div className="as-toolbar">
          <div className="as-filter-wrap">
            <i className="fas fa-cloud as-filter-icon" />
            <select
              className="as-filter-select"
              value={provider}
              onChange={e => { setProvider(e.target.value); setPage(0); }}
            >
              <option value="ALL">{t("Assets.allVendors")}</option>
              {providers.map(p => <option key={p.name} value={p.name}>{p.name} ({p.count})</option>)}
            </select>
          </div>
          <div className="as-filter-wrap">
            <i className="fas fa-layer-group as-filter-icon" />
            <select
              className="as-filter-select"
              value={layer}
              onChange={e => { setLayer(e.target.value); setPage(0); }}
            >
              {LAYERS.map(l => <option key={l} value={l}>{l === "ALL" ? t("Assets.allLayers") : l}</option>)}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="as-table-wrap">
          <table className="as-table">
            <thead>
              <tr>
                <th>{t("Assets.colAssetName")}</th>
                <th>{t("Assets.colType")}</th>
                <th>{t("Assets.colVendor")}</th>
                <th>{t("Assets.colLayer")}</th>
                <th>{t("Assets.colRisk")}</th>
                <th>{t("Assets.colStatus")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {assets.length === 0 ? (
                <tr><td colSpan={7} className="as-empty">{t("Assets.noAssetsFound")}</td></tr>
              ) : (
                assets.map(asset => (
                  <React.Fragment key={asset.id}>
                    <tr className="as-row">
                      <td className="as-name">{asset.displayName || asset.name}</td>
                      <td><span className="as-type-badge">{(asset.assetType || "").replace(/_/g, " ")}</span></td>
                      <td className="as-owner">{asset.cloudProvider}</td>
                      <td>{asset.layer}</td>
                      <td>
                        {hasRiskScore(asset)
                          ? <span className={`as-status ${riskClass(asset.riskScore || 0)}`}>{asset.riskScore ?? 0}</span>
                          : <span className="as-status-muted" title={t("Assets.riskNotScored")}>{t("Assets.riskNA")}</span>}
                      </td>
                      <td>
                        <span className={`as-status ${asset.status === "ACTIVE" ? "as-active" : "as-review"}`}>
                          {asset.status === "ACTIVE" ? t("Assets.statusActive") : (asset.status || "—").replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="as-actions">
                        <button
                          className="as-icon-btn"
                          title={t("Assets.viewDetails")}
                          onClick={() => setOpen(open === asset.id ? null : asset.id)}
                        >
                          <i className={`fas fa-chevron-${open === asset.id ? "up" : "down"}`} />
                        </button>
                      </td>
                    </tr>
                    {open === asset.id && (
                      <tr className="as-detail-row">
                        <td colSpan={7}>
                          <div className="as-detail-content">
                            <div className="as-detail-section">
                              <p className="as-detail-heading">{t("Assets.assetDetails")}</p>
                              <ul className="as-detail-list">
                                {asset.region   && <li><i className="fas fa-map-marker-alt" /> {t("Assets.region", { region: asset.region })}</li>}
                                {asset.accountId && <li><i className="fas fa-id-card" /> {t("Assets.account", { account: asset.accountId })}</li>}
                                {hasRiskScore(asset) && (
                                  <li><i className="fas fa-bomb" /> {t("Assets.blastRadius", { value: asset.blastRadius ?? 0 })}</li>
                                )}
                                {asset.externalId && <li><i className="fas fa-fingerprint" /> {asset.externalId}</li>}
                              </ul>
                            </div>
                            {asset.metadata && Object.keys(asset.metadata).length > 0 && (
                              <div className="as-detail-section">
                                <p className="as-detail-heading">{t("Assets.metadata")}</p>
                                <ul className="as-detail-list">
                                  {Object.entries(asset.metadata).slice(0, 10).map(([k, v]) => (
                                    <li key={k}><i className="fas fa-info-circle" /> {k}: {typeof v === "object" ? JSON.stringify(v) : String(v)}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination (server-side) */}
        <div className="as-pagination">
          <div className="as-page-info">
            <span>{t("Assets.assetsCount", { shown: assets.length, total })}</span>
            <select
              className="as-per-page"
              value={size}
              onChange={e => { setSize(Number(e.target.value)); setPage(0); }}
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{t("Assets.perPage", { count: n })}</option>)}
            </select>
          </div>
          <div className="as-page-controls">
            <button className="as-page-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <i className="fas fa-chevron-left" />
            </button>
            <span className="as-page-num">{t("Assets.pageNum", { page: page + 1 })}</span>
            <button className="as-page-btn" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * size >= total}>
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Assets;
